/**
 * Cerebro: notas, ideas, enlaces, fechas importantes y listas (entidad notes).
 * Funciones puras de búsqueda y acciones de escritura a través del repositorio.
 */
import type { Note } from '@habitos/protocol';
import { write, writeMany } from '../db/repo';
import { keyAfter } from '../lib/fractional';
import { uuidv7 } from '../lib/ids';
import type { ListItemRec } from './model';

export {
  allTags,
  findList,
  itemsOf,
  normalize,
  NOTE_KINDS,
  notesLinkedTo,
  parseTags,
  searchNotes,
  sortNotes,
  type NoteFilter,
  type NoteKind,
} from '@habitos/assistant/domain/notesSearch';
import { itemsOf, type NoteKind } from '@habitos/assistant/domain/notesSearch';

export interface NoteDraft {
  kind: NoteKind;
  title: string;
  body?: string | null;
  url?: string | null;
  date?: string | null;
  tags?: string[];
  habitIds?: string[];
  pinned?: boolean;
}

export async function createNote(d: NoteDraft): Promise<string> {
  const id = uuidv7();
  await write('notes', id, {
    kind: d.kind,
    title: d.title.trim(),
    body: d.body?.trim() || null,
    url: d.kind === 'link' ? d.url?.trim() || null : null,
    date: d.kind === 'date' ? d.date || null : null,
    tags: d.tags ?? [],
    habitIds: d.habitIds ?? [],
    pinned: d.pinned ?? false,
    createdAt: new Date().toISOString(),
    deletedAt: null,
  });
  return id;
}

export async function updateNote(id: string, patch: Partial<Omit<Note, 'createdAt'>>): Promise<void> {
  await write('notes', id, patch);
}

/** Borrado suave: se puede deshacer con restoreNote. */
export async function deleteNote(id: string): Promise<void> {
  await write('notes', id, { deletedAt: new Date().toISOString() });
}

export async function restoreNote(id: string): Promise<void> {
  await write('notes', id, { deletedAt: null });
}

/** Añade elementos al final de la lista. Devuelve sus identificadores (para deshacer). */
export async function addListItems(listId: string, texts: string[], current: ListItemRec[]): Promise<string[]> {
  let last = itemsOf(current, listId).pop()?.sortKey ?? null;
  const now = new Date().toISOString();
  const requests = texts
    .map((t) => t.trim())
    .filter(Boolean)
    .map((text) => {
      last = keyAfter(last);
      return { entity: 'listItems' as const, id: uuidv7(), patch: { listId, text: text.slice(0, 200), done: false, sortKey: last, createdAt: now, deletedAt: null } };
    });
  await writeMany(requests);
  return requests.map((r) => r.id);
}

export async function setItemDone(id: string, done: boolean): Promise<void> {
  await write('listItems', id, { done });
}

export async function deleteListItems(ids: string[]): Promise<void> {
  const at = new Date().toISOString();
  await writeMany(ids.map((id) => ({ entity: 'listItems' as const, id, patch: { deletedAt: at } })));
}

export async function restoreListItems(ids: string[]): Promise<void> {
  await writeMany(ids.map((id) => ({ entity: 'listItems' as const, id, patch: { deletedAt: null } })));
}
